import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement | undefined} cartEpCount - Optional visible count span for Catalog / EP Cart(n) styling.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {MediaQueryList | null} */
  #epMobileBubbleMql = null;

  /** @returns {boolean} */
  #isEpLabelMode() {
    return this.hasAttribute('data-ep-cart-label');
  }

  /** @returns {boolean} */
  #isEpMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * EP mode: Cart(n) on desktop; count badge only on mobile. Desktop hiding is reinforced in CSS.
   * @param {number} itemCount
   */
  #syncEpMobileBubbleVisibility(itemCount) {
    if (!this.#isEpLabelMode()) return;
    if (!this.#isEpMobileViewport()) return;
    const show = itemCount > 0;
    this.refs.cartBubble.classList.toggle('visually-hidden', !show);
    this.refs.cartBubbleCount.classList.toggle('hidden', !show);
  }

  #onEpMobileBubbleViewportChange = () => {
    const n = parseInt(this.dataset.cartItemCount || '0', 10) || 0;
    this.#syncEpMobileBubbleVisibility(n);
  };

  /** @param {number} n */
  #setCartItemCountDataset(n) {
    this.dataset.cartItemCount = String(n);
  }

  #syncCartItemCountDatasetFromDom() {
    const ep = this.refs.cartEpCount?.textContent?.trim();
    const bubble = this.refs.cartBubbleCount?.textContent?.trim();
    const raw =
      ep && ep !== '' && ep !== '99+' ? ep : bubble && bubble !== '' ? bubble : ep === '99+' ? '' : '';

    const n = parseInt(raw || '0', 10);
    this.#setCartItemCountDataset(Number.isFinite(n) ? n : 0);
  }

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    if (this.#isEpLabelMode()) {
      this.#epMobileBubbleMql = window.matchMedia('(max-width: 749px)');
      this.#epMobileBubbleMql.addEventListener('change', this.#onEpMobileBubbleViewportChange);
    }
    this.#syncCartItemCountDatasetFromDom();
    if (this.#isEpLabelMode()) {
      const initial = parseInt(this.dataset.cartItemCount || this.refs.cartBubbleCount?.textContent || '0', 10) || 0;
      this.#syncEpMobileBubbleVisibility(initial);
    }
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#epMobileBubbleMql?.removeEventListener('change', this.#onEpMobileBubbleViewportChange);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const ep = this.#isEpLabelMode();
    const prev = comingFromProductForm ? parseInt(this.dataset.cartItemCount || '0', 10) || 0 : 0;
    const newCount = comingFromProductForm ? prev + itemCount : itemCount;

    if (!ep) {
      this.refs.cartBubbleCount.classList.toggle('hidden', newCount === 0);
      this.refs.cartBubble.classList.toggle('visually-hidden', newCount === 0);
    }

    this.currentCartCount = newCount;

    if (this.refs.cartEpCount) {
      this.refs.cartEpCount.textContent = newCount >= 100 ? '99+' : String(newCount);
    }

    this.classList.toggle('header-actions__cart-icon--has-cart', newCount > 0);

    if (ep) {
      this.#syncEpMobileBubbleVisibility(newCount);
    }

    this.#setCartItemCountDataset(newCount);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || newCount === 0) return;
    if (ep && !this.#isEpMobileViewport()) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
